import { useState, useCallback } from 'react';

const STORAGE_KEY = 'jama_driver_onboarding';

const initialFormData = {
  // Información personal
  first_name: '',
  last_name: '',
  national_id: '',
  // Información de contacto
  phone: '',
  email: '',
  city_id: null,
  // Tipo de aplicante (siempre driver)
  applicant_type: 'driver',
  // Foto tamano carne (fondo blanco) - una sola imagen, sin OCR
  photo: null,
  // Documentos (archivos) - ahora con 2 fotos por documento (frontal y reverso)
  documents: {
    national_id: {
      front: null,
      back: null,
      validated: false,
      validationData: null,
    },
    license: {
      front: null,
      back: null,
      validated: false,
      validationData: null,
    },
    registration: {
      front: null,
      back: null,
      validated: false,
      validationData: null,
    },
  },
  // Estado de la aplicación
  applicationId: null,
  applicationStatus: null,
};

export const useOnboardingForm = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState(() => {
    // Intentar recuperar datos guardados
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        // No restaurar archivos (no se pueden serializar)
        return {
          ...parsed,
          photo: null,
          documents: {
            national_id: {
              front: null,
              back: null,
              validated: false,
              validationData: null,
            },
            license: {
              front: null,
              back: null,
              validated: false,
              validationData: null,
            },
            registration: {
              front: null,
              back: null,
              validated: false,
              validationData: null,
            },
          },
        };
      }
    } catch (error) {
      console.error('Error loading saved form data:', error);
    }
    return initialFormData;
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Guardar progreso en localStorage
  const saveProgress = useCallback((data) => {
    try {
      const toSave = {
        ...data,
        photo: null,
        documents: {
          national_id: {
            front: null,
            back: null,
            validated: data.documents.national_id?.validated || false,
            validationData: null,
          },
          license: {
            front: null,
            back: null,
            validated: data.documents.license?.validated || false,
            validationData: null,
          },
          registration: {
            front: null,
            back: null,
            validated: data.documents.registration?.validated || false,
            validationData: null,
          },
        },
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(toSave));
    } catch (error) {
      console.error('Error saving form data:', error);
    }
  }, []);

  // Actualizar datos del formulario
  const updateFormData = useCallback((updates) => {
    setFormData((prev) => {
      const newData = { ...prev, ...updates };
      saveProgress(newData);
      return newData;
    });
  }, [saveProgress]);

  // Actualizar documentos - ahora soporta front/back y validación
  const updateDocuments = useCallback((documentType, side, file) => {
    setFormData((prev) => ({
      ...prev,
      documents: {
        ...prev.documents,
        [documentType]: {
          ...prev.documents[documentType],
          [side]: file,
        },
      },
    }));
  }, []);

  // Marcar documento como validado
  const setDocumentValidated = useCallback((documentType, validated, validationData = null) => {
    setFormData((prev) => ({
      ...prev,
      documents: {
        ...prev.documents,
        [documentType]: {
          ...prev.documents[documentType],
          validated,
          validationData,
        },
      },
    }));
  }, []);

  // Navegación
  const nextStep = useCallback(() => {
    setCurrentStep((prev) => prev + 1);
    setError(null);
  }, []);

  const prevStep = useCallback(() => {
    setCurrentStep((prev) => Math.max(0, prev - 1));
    setError(null);
  }, []);

  const goToStep = useCallback((step) => {
    setCurrentStep(step);
    setError(null);
  }, []);

  // Limpiar formulario
  const resetForm = useCallback(() => {
    setFormData(initialFormData);
    setCurrentStep(0);
    setError(null);
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  // Establecer ID de aplicación
  const setApplicationId = useCallback((id) => {
    setFormData((prev) => {
      const newData = { ...prev, applicationId: id };
      saveProgress(newData);
      return newData;
    });
  }, [saveProgress]);

  return {
    currentStep,
    formData,
    isSubmitting,
    error,
    setIsSubmitting,
    setError,
    updateFormData,
    updateDocuments,
    setDocumentValidated,
    nextStep,
    prevStep,
    goToStep,
    resetForm,
    setApplicationId,
  };
};

export default useOnboardingForm;
